import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import { type ComponentProps, useEffect, useRef } from 'react';

import { cn } from '../lib/cn';

export interface SubNavProps extends Omit<ComponentProps<'nav'>, 'aria-label'> {
  /** 보조기술이 듣는 이름. 예: "My Lab 메뉴" */
  label: string;
}

/**
 * 영역 안 2차 이동 — 링크 목록이지 `Tabs`가 아니다(주소가 바뀌는 이동 · D145 ⑨).
 * 넘치면 가로로 스크롤하고 현재 항목이 늘 보이게 한다. 현재 표시는 즉시 바뀐다(D145 ⑩).
 */
function SubNavRoot({ label, className, children, ...props }: SubNavProps) {
  const listRef = useRef<HTMLUListElement>(null);
  const shownRef = useRef<HTMLElement | null>(null);

  /* 현재 항목이 바뀔 때만, 가로 목록 안에서만 옮긴다 — 사용자가 넘겨 보던 위치를 렌더마다 되감지 않는다. */
  useEffect(() => {
    const list = listRef.current;
    const current = list?.querySelector<HTMLElement>('[aria-current="page"]') ?? null;
    if (!list || !current || current === shownRef.current) return;
    shownRef.current = current;
    const start = current.offsetLeft - list.offsetLeft;
    const end = start + current.offsetWidth;
    if (start < list.scrollLeft) list.scrollLeft = start;
    else if (end > list.scrollLeft + list.clientWidth) list.scrollLeft = end - list.clientWidth;
  });

  return (
    <nav aria-label={label} className={cn('border-line border-b', className)} {...props}>
      <ul ref={listRef} className="px-gutter flex items-stretch overflow-x-auto">
        {children}
      </ul>
    </nav>
  );
}

export interface SubNavLinkProps extends useRender.ComponentProps<'a'> {
  /** 지금 이 화면인가 — `aria-current="page"`와 아래 2px 줄을 함께 켠다. */
  current?: boolean;
}

/**
 * 라우터 링크는 `render`로 넘긴다 — `<SubNav.Link render={<Link to="/mylab" />}>오늘</SubNav.Link>`.
 * 현재 표시는 `aria-current="page"` 속성에서 읽으므로 라우터가 붙인 속성도 그대로 통한다.
 */
export function SubNavLink({ current = false, render, className, ...props }: SubNavLinkProps) {
  const element = useRender({
    defaultTagName: 'a',
    render,
    props: mergeProps<'a'>(
      {
        'aria-current': current ? 'page' : undefined,
        className: cn(
          'px-row py-row relative block text-sm font-medium whitespace-nowrap',
          'ease-flow duration-fast transition-colors',
          'text-ink-muted hover:text-ink aria-[current=page]:text-action',
          'aria-[current=page]:after:bg-selected after:absolute after:inset-x-0 after:bottom-0 after:h-0.5',
          className,
        ),
      },
      props,
    ),
  });

  return <li className="flex">{element}</li>;
}

/** 그룹 사이 구분선. 그룹 이름은 붙이지 않는다(D145 ②). */
export function SubNavDivider({ className, ...props }: ComponentProps<'li'>) {
  return (
    <li
      aria-hidden="true"
      className={cn('bg-line mx-snug my-row w-px shrink-0', className)}
      {...props}
    />
  );
}

export const SubNav = Object.assign(SubNavRoot, { Link: SubNavLink, Divider: SubNavDivider });
